/**
 * Phase F11 FE-2b2 — screen-share consent + acquisition gate.
 *
 * Sits between :class:`IdDocumentCapture` and :class:`StartGate` for
 * proctored attempts. Requires the applicant to share their entire
 * screen ("Entire Screen" pick in the OS picker) so :func:`useScreenCaptureLoop`
 * can capture periodic stills of the desktop alongside the webcam loop.
 *
 * Why a dedicated gate (and not just a useEffect):
 *   ``navigator.mediaDevices.getDisplayMedia`` is a user-gesture-only
 *   API. Modern browsers refuse it with ``InvalidStateError`` if it's
 *   invoked outside a click/keydown handler. So we render a button,
 *   bind the API call to its click handler, and lift the resulting
 *   stream up via prop.
 *
 * On success: the parent stashes the stream + flips the gate to
 * pass-through so the StartGate can render. The stream is owned by
 * the parent for the lifetime of the attempt; this component just
 * acquires it.
 *
 * Failure modes:
 *   * Permission denied / picker dismissed → render an inline error
 *     and let the applicant retry. We don't auto-advance.
 *   * Browser unsupported → render a clear message; there's no
 *     fallback (the proctor profile required screen-share, so we
 *     can't ship the applicant past this gate).
 *
 * Privacy: we only ask for ``video``; ``audio`` is omitted so we
 * don't accidentally capture system audio. The browser's own
 * "you are sharing your screen" indicator stays on for the entire
 * attempt — we don't suppress it (and couldn't even if we wanted to).
 */
import * as React from "react";
import { motion } from "motion/react";
import { AlertTriangle, Monitor, ShieldCheck } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";

import { PageCard } from "@/components/AppFrame";
import type { ProctorClient } from "@/lib/proctor-client";

export interface ScreenCaptureGateProps {
  client: ProctorClient;
  /** Called after the applicant grants screen share. The parent owns
   * the stream from that point on (and is responsible for stopping
   * its tracks at attempt-end). */
  onScreenStreamReady: (stream: MediaStream) => void;
}

type GateState =
  | { kind: "idle" }
  | { kind: "requesting" }
  | { kind: "denied"; reason: ScreenCaptureDeniedReason };

export type ScreenCaptureDeniedReason =
  | "permission_denied"
  | "unsupported_browser"
  | "transient_error";

const _DENIED_COPY: Record<ScreenCaptureDeniedReason, string> = {
  permission_denied:
    'We need access to your screen to continue. Please pick "Entire Screen" in the share picker, then try again.',
  unsupported_browser:
    "Your browser doesn't support screen sharing. Please use the latest Chrome, Edge, or Firefox on a laptop or desktop.",
  transient_error:
    "Something went wrong while requesting screen share. Please try again — if the problem persists, restart the browser.",
};

export function ScreenCaptureGate({ client, onScreenStreamReady }: ScreenCaptureGateProps) {
  const [state, setState] = React.useState<GateState>({ kind: "idle" });

  const requestShare = React.useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getDisplayMedia) {
      setState({ kind: "denied", reason: "unsupported_browser" });
      return;
    }
    setState({ kind: "requesting" });
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 5 },
        },
        audio: false,
      });
      // Best-effort consent telemetry. Failures are swallowed by the
      // proctor client itself so they don't bubble up here.
      void client.recordEvent("consent_granted", { stream: "screen" });
      onScreenStreamReady(stream);
    } catch (err) {
      const reason = classifyDisplayMediaError(err);
      setState({ kind: "denied", reason });
      void client.recordEvent("consent_denied", {
        reason: `screen_gate_${reason}`,
      });
    }
  }, [client, onScreenStreamReady]);

  return (
    <PageCard
      title="Share your screen"
      subtitle="So our proctoring system can verify you aren't reading off another window during the assessment."
    >
      <motion.div
        className="border-border bg-muted/40 flex flex-col gap-4 rounded-md border p-4"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
      >
        <div className="flex items-start gap-3">
          <Monitor className="text-primary mt-0.5 h-5 w-5 flex-shrink-0" />
          <p className="text-sm leading-relaxed">
            When you click the button below, your browser will ask which screen to share.{" "}
            <strong>Pick "Entire Screen"</strong> — choosing a single window or tab will not work.
          </p>
        </div>

        <div className="flex items-start gap-3">
          <ShieldCheck className="text-primary mt-0.5 h-5 w-5 flex-shrink-0" />
          <p className="text-muted-foreground text-sm leading-relaxed">
            Your browser will show a "you are sharing your screen" indicator the whole time. We
            capture occasional stills only — no audio, no continuous video, no keystroke logging.
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            onClick={() => void requestShare()}
            disabled={state.kind === "requesting"}
          >
            {state.kind === "requesting" ? "Waiting for permission…" : "Share my screen"}
          </Button>
        </div>
      </motion.div>

      {state.kind === "denied" && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive mt-4 flex items-start gap-2 rounded-md border p-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{_DENIED_COPY[state.reason]}</span>
        </div>
      )}
    </PageCard>
  );
}

function classifyDisplayMediaError(err: unknown): ScreenCaptureDeniedReason {
  if (err === null || typeof err !== "object" || !("name" in err)) {
    return "transient_error";
  }
  const name = String((err as { name: unknown }).name);
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
    case "AbortError":
      return "permission_denied";
    case "NotFoundError":
    case "NotSupportedError":
    case "TypeError":
      return "unsupported_browser";
    default:
      return "transient_error";
  }
}
